import { FC, JSX, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import Select, { InputSearch } from '@semcore/select';
import Spin from '@semcore/spin';

import { useGetContentStylesQuery } from '@/entities/content/api/contentApi';
import type { Content, ContentStyles } from '@/entities/content/api/types';

import Option from './Option';

import s from '../SideForm.module.css';

interface ISelectorProps {
  selectedPresetId?: ContentStyles['id'];
  setSelectedPresetId: (id: ContentStyles['id']) => void;
}

const Selector: FC<ISelectorProps> = ({
  selectedPresetId,
  setSelectedPresetId,
}): JSX.Element => {
  const { setValue } = useFormContext<Content>();
  const { isLoading, data: { data: presets = [] } = {} } =
    useGetContentStylesQuery();
  const [filter, setFilter] = useState<ContentStyles['name'] | ''>('');
  const options = useMemo(
    () =>
      presets.filter((preset) =>
        preset.name.toString().toLowerCase().includes(filter.toLowerCase())
      ),
    [filter, presets]
  );

  return (
    <div className={s.psc}>
      <Select
        placeholder="Preset"
        size="l"
        display="flex"
        value={selectedPresetId}
        onChange={(value: ContentStyles['id']) => {
          setSelectedPresetId(value);
          const preset = presets.filter(({ id }) => id === value);

          if (preset.length) {
            setValue('config', preset[0].config);
          }
        }}
        css={{ width: '100%' }}
      >
        {isLoading ? (
          <Select.Trigger
            mt={2}
            mr="auto"
            id="loading-select-no-chevron"
            chevron={false}
            placeholder={<Spin size="xs" mx={4} />}
            tabIndex={-1}
          />
        ) : (
          <Select.Trigger id="options-filtering-advanced" display="flex">
            {presets
              .filter(({ id }) => id === selectedPresetId)
              .map((preset) => (
                <div className="flex flex-col gap-0.5" key={preset.id}>
                  <div className="text-xs font-medium text-[#18192b]/[0.4] dark:text-white/[0.4]">
                    Preset
                  </div>
                  <Option key={preset.id} data={preset} />
                </div>
              ))}
          </Select.Trigger>
        )}
        <Select.Popper>
          <InputSearch value={filter} onChange={setFilter}>
            <InputSearch.SearchIcon />
            <InputSearch.Value placeholder="Search by name" />
            <InputSearch.Clear
              onClick={() => {
                return false;
              }}
            />
          </InputSearch>
          <Select.List hMax="224px" display="flex">
            {options.map((preset) => (
              <Select.Option value={preset.id} key={preset.id}>
                <Option data={preset} />
              </Select.Option>
            ))}
            {options.length ? (
              <Select.OptionHint id="search-result-advanced">
                <span className="text-sm font-light text-secondary">
                  {options.length} result{options.length > 1 && 's'} found
                </span>
              </Select.OptionHint>
            ) : (
              <Select.OptionHint id="search-result-advanced" key="Nothing">
                <span className="text-sm font-light text-secondary">
                  Nothing found
                </span>
              </Select.OptionHint>
            )}
          </Select.List>
        </Select.Popper>
      </Select>
    </div>
  );
};

export default Selector;
